import { Button } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { type FileType, getFileType } from "@utils/assets";
import { HiddenInput } from "@ui-kit/hidden-input";

const validateImageRatio = async (
  file: File,
  isSizeValid: ({
    width,
    height,
  }: {
    width: number;
    height: number;
  }) => boolean,
): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const valid = isSizeValid({ width: img.width, height: img.height });
      resolve(valid);
    };
    img.onerror = () => {
      reject(new Error("Failed to load image for validation"));
    };
    img.src = URL.createObjectURL(file);
  });
};

type Props = {
  label: string;
  onChange: (file: File) => void;
  onError: (message: string) => void;
  uploading: boolean;
  allowedFileTypes?: FileType[];
  maxSizeKb: number;
  isSizeValid: (args: { width: number; height: number }) => boolean;
  fullWidth?: boolean;
};

export const UploadFileButton = ({
  label,
  onChange,
  onError,
  uploading,
  allowedFileTypes = [],
  maxSizeKb,
  isSizeValid,
  fullWidth,
}: Props) => {
  return (
    <Button
      component="label"
      role={undefined}
      variant="contained"
      tabIndex={-1}
      startIcon={<CloudUploadIcon />}
      disabled={uploading}
      fullWidth={fullWidth}
    >
      {label}
      <HiddenInput
        type="file"
        accept={allowedFileTypes.join(",")}
        onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
          try {
            const file = event.target.files ? event.target.files[0] : undefined;
            if (!file) {
              onError("No file selected");
              return;
            }
            const fileType = getFileType(file);
            if (allowedFileTypes && !allowedFileTypes.includes(fileType)) {
              onError(
                `File type not allowed. Allowed types: ${allowedFileTypes.join(", ")}`,
              );
              return;
            }
            if (file.size > maxSizeKb * 1024) {
              onError(`File too large, maximum size is ${maxSizeKb}KB`);
              return;
            }
            if (file.type.startsWith("image/")) {
              const valid = await validateImageRatio(file, isSizeValid);
              if (!valid) {
                onError("Invalid image ratio");
                return;
              }
            }
            onChange(file);
          } catch (e) {
            const err = e as Error;
            onError(err.message);
          }
        }}
      />
    </Button>
  );
};
